// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { HasPagingParameters } from "@insite/client-framework/Services/ApiService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import { OpenBidsApiParameter, OpenBidsModel } from "@steiner/App/Services/OpenBidsService";
import { Draft } from "immer";

export interface OpenBidOrderModel {
    orderId?: string;
    orderDate?: string;
    shipDate?: string;
    bidNumber?: string;
    customerPo?: string;
    bidExpireDate?: string;
    customerReleaseNumber?: string;
    amount?: number;
    generationId?: string;
    shippingInformation?: {
        shipVia?: { description?: string; shipViaId?: string };
    };
}

export interface OpenBidsSearchParams {
    customerSequence?: string;
    poNumber?: string;
    orderNumber?: string;
    releaseNumber?: string;
    fromDate?: string;
    toDate?: string;
}

export type OpenBidsState = {
    orders?: OpenBidOrderModel[];
    pagination: PaginationModel | null;
    isLoading?: boolean;
    searchParams?: OpenBidsApiParameter;
};

const initialState: OpenBidsState = {
    pagination: null,
    isLoading: false,
    searchParams: {
        customerSequence: "-1",
        poNumber: "",
        orderNumber: "",
        releaseNumber: "",
        fromDate: "",
        toDate: "",
    },
};

const xOpenBids = createTypedReducerWithImmer(initialState, {
    "xOpenBids/BeginLoadingOpenBids": (draft: Draft<OpenBidsState>, action: { isLoading: boolean }) => {
        draft.isLoading = action.isLoading;
    },
    "xOpenBids/FailedLoadingOpenBids": (draft: Draft<OpenBidsState>) => {
        draft.isLoading = false;
    },
    "xOpenBids/CompleteLoadingOpenBids": (draft: Draft<OpenBidsState>, action: { value: OpenBidsModel }) => {
        draft.orders = action.value?.openOrderItemList;
        draft.pagination = action.value?.pagination;
        draft.isLoading = false;
    },
    "xOpenBids/UpdateSearchParams": (
        draft: Draft<OpenBidsState>,
        action: { parameter: { key: string; value: string | number } },
    ) => {
        const { key, value } = action.parameter;
        // Update the searchParams with the new key and value
        draft.searchParams = { ...draft.searchParams, [key]: value };

        // Remove empty or undefined parameters
        for (const key in draft.searchParams) {
            const value = (draft.searchParams as any)[key];

            if (value === "" || value === undefined) {
                delete (draft.searchParams as any)[key];
            }
        }

        // If page size or page is changed, reset the page to 1
        if (
            draft.pagination &&
            draft.pagination.page &&
            draft.pagination.page > 1 &&
            key !== "page" &&
            key !== "pageSize"
        ) {
            draft.pagination.page = 1;
        }
    },
    "xOpenBids/ClearSearchParams": (draft: Draft<OpenBidsState>) => {
        draft.searchParams = { ...initialState.searchParams };
    },
});

(reducers as any).xOpenBids = xOpenBids;

export type xOpenBidsState = {
    xOpenBids: OpenBidsState;
} & ApplicationState;

type OpenBidsReducer = {
    openBids: typeof xOpenBids;
};

export type openBidsDataActions = Parameters<OpenBidsReducer[keyof OpenBidsReducer]>[1] | AnyAction;
